import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiComparisonService, ComparisonResult } from '../_services/api-comparison.service';
import { HttpClient } from '@angular/common/http';
import { ApiSwitcherService } from '../_services/api-switcher.service';

@Injectable({
  providedIn: 'root'
})
export class ComparisonInterceptor implements HttpInterceptor {
  constructor(
    private apiComparisonService: ApiComparisonService,
    private apiSwitcher: ApiSwitcherService,
    private http: HttpClient
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip si comparaison désactivée
    if (!environment.enableApiComparison) {
      return next.handle(request);
    }

    // Déterminer quelle API est primaire et quelle est secondaire
    const nodeUrl = this.apiSwitcher.nodeApiUrl;
    const phpUrl = this.apiSwitcher.phpApiUrl;

    // Skip si pas les deux URLs configurées
    if (!nodeUrl || !phpUrl || nodeUrl === phpUrl) {
      return next.handle(request);
    }

    // Déterminer si la requête va vers l'API active (primaire)
    const activeUrl = environment.apiUrl;
    if (!request.url.startsWith(activeUrl)) {
      return next.handle(request);
    }

    // L'autre API = celle qui n'est pas active
    const secondaryUrl = (activeUrl === nodeUrl) ? phpUrl : nodeUrl;
    const primaryIsNode = (activeUrl === nodeUrl);

    // Skip pour les endpoints spéciaux
    const skipEndpoints = ['/auth/', '/login', '/signin', '/filebypath', '/comparison/'];
    if (skipEndpoints.some(ep => request.url.includes(ep))) {
      return next.handle(request);
    }

    // Ne PAS exécuter les POST/PATCH/PUT/DELETE sur l'API secondaire (éviter doublons)
    // Logger uniquement dans le rapport pour vérification manuelle
    const writeMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    const isWrite = writeMethods.includes(request.method.toUpperCase());
    if (isWrite) {
      const startTime = Date.now();
      return next.handle(request).pipe(
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            const endpoint = request.url.replace(activeUrl, '');
            const result: ComparisonResult = {
              id: this.apiComparisonService.generateId(),
              timestamp: new Date(),
              method: request.method,
              endpoint,
              nodeStatus: primaryIsNode ? event.status : 0,
              phpStatus: primaryIsNode ? 0 : event.status,
              isMatch: false,
              nodeDuration: primaryIsNode ? (Date.now() - startTime) : 0,
              phpDuration: primaryIsNode ? 0 : (Date.now() - startTime),
              diff: { statusMismatch: true },
              nodeResponse: primaryIsNode ? event.body : undefined,
              phpResponse: primaryIsNode ? undefined : event.body,
              phpError: primaryIsNode ? 'SKIPPED_IN_PROD (write operation)' : undefined
            };
            if (!primaryIsNode) {
              (result as any).nodeError = 'SKIPPED_IN_PROD (write operation)';
            }
            this.apiComparisonService.addResult(result);
            this.sendToServer(result, request);
          }
        })
      );
    }

    const startTimePrimary = Date.now();

    // Exécuter la requête primaire normalement
    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const primaryResponse = event.body;
          const primaryStatus = event.status;
          const primaryDuration = Date.now() - startTimePrimary;
          const endpoint = request.url.replace(activeUrl, '');

          // Dupliquer vers l'API secondaire
          this.compareWithSecondary(
            request, endpoint, secondaryUrl, primaryIsNode,
            primaryResponse, primaryStatus, primaryDuration
          );
        }
      })
    );
  }

  /**
   * Effectue la requête vers l'API secondaire et compare les résultats
   */
  private compareWithSecondary(
    originalRequest: HttpRequest<any>,
    endpoint: string,
    secondaryUrl: string,
    primaryIsNode: boolean,
    primaryResponse: any,
    primaryStatus: number,
    primaryDuration: number
  ): void {
    const secondaryFullUrl = originalRequest.url.replace(environment.apiUrl, secondaryUrl);
    const startTimeSecondary = Date.now();

    this.http.request(originalRequest.method, secondaryFullUrl, {
      body: originalRequest.body,
      headers: originalRequest.headers,
      params: originalRequest.params,
      observe: 'response',
      responseType: 'json'
    }).pipe(
      timeout(10000),
      catchError((error: HttpErrorResponse) => {
        const secondaryDuration = Date.now() - startTimeSecondary;
        const result: ComparisonResult = {
          id: this.apiComparisonService.generateId(),
          timestamp: new Date(),
          method: originalRequest.method,
          endpoint,
          // Toujours normaliser : nodeStatus/phpStatus quel que soit le sens
          nodeStatus: primaryIsNode ? primaryStatus : (error.status || 0),
          phpStatus: primaryIsNode ? (error.status || 0) : primaryStatus,
          isMatch: false,
          nodeDuration: primaryIsNode ? primaryDuration : secondaryDuration,
          phpDuration: primaryIsNode ? secondaryDuration : primaryDuration,
          nodeResponse: primaryIsNode ? primaryResponse : undefined,
          phpError: primaryIsNode
            ? (error.message || 'PHP request failed')
            : undefined,
          diff: { statusMismatch: true }
        };
        // Si Node.js est secondaire et échoue
        if (!primaryIsNode) {
          (result as any).nodeError = error.message || 'Node.js request failed';
          result.phpResponse = primaryResponse;
        }
        this.apiComparisonService.addResult(result);
        this.sendToServer(result, originalRequest);
        return of(null);
      })
    ).subscribe((secondaryHttpResponse: HttpResponse<any> | null) => {
      if (!secondaryHttpResponse) return;

      const secondaryDuration = Date.now() - startTimeSecondary;
      const secondaryResponse = secondaryHttpResponse.body;
      const secondaryStatus = secondaryHttpResponse.status;

      // Normaliser en node/php
      const nodeResponse = primaryIsNode ? primaryResponse : secondaryResponse;
      const phpResponse = primaryIsNode ? secondaryResponse : primaryResponse;
      const nodeStatus = primaryIsNode ? primaryStatus : secondaryStatus;
      const phpStatus = primaryIsNode ? secondaryStatus : primaryStatus;

      let comparisonResult: { isMatch: boolean; diff?: ComparisonResult['diff'] };
      if (nodeStatus !== phpStatus) {
        comparisonResult = { isMatch: false, diff: { statusMismatch: true } };
      } else {
        comparisonResult = this.apiComparisonService.compareResponses(nodeResponse, phpResponse);
      }

      const result: ComparisonResult = {
        id: this.apiComparisonService.generateId(),
        timestamp: new Date(),
        method: originalRequest.method,
        endpoint,
        nodeStatus,
        phpStatus,
        isMatch: comparisonResult.isMatch,
        nodeDuration: primaryIsNode ? primaryDuration : secondaryDuration,
        phpDuration: primaryIsNode ? secondaryDuration : primaryDuration,
        diff: comparisonResult.diff,
        nodeResponse,
        phpResponse
      };

      this.apiComparisonService.addResult(result);

      if (!result.isMatch) {
        this.sendToServer(result, originalRequest);
      }
    });
  }

  /**
   * Envoie le résultat de comparaison au serveur Node.js pour logging/rapport
   */
  private sendToServer(result: ComparisonResult, originalRequest: HttpRequest<any>): void {
    const nodeApiUrl = this.apiSwitcher.nodeApiUrl;
    const reportUrl = `${nodeApiUrl}/comparison/report`;

    let queryParams: Record<string, string> = {};
    try {
      const url = new URL(originalRequest.urlWithParams);
      url.searchParams.forEach((value, key) => { queryParams[key] = value; });
    } catch { /* ignore */ }

    const payload = {
      timestamp: result.timestamp,
      method: result.method,
      endpoint: result.endpoint,
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : null,
      body: originalRequest.body ? this.summarize(originalRequest.body) : null,
      nodeStatus: result.nodeStatus,
      phpStatus: result.phpStatus,
      isMatch: result.isMatch,
      nodeDuration: result.nodeDuration,
      phpDuration: result.phpDuration,
      diff: result.diff,
      phpError: result.phpError,
      nodeResponse: this.summarize(result.nodeResponse),
      phpResponse: this.summarize(result.phpResponse)
    };

    // Fire and forget
    this.http.post(reportUrl, payload).subscribe({
      error: (err) => console.warn('[ComparisonInterceptor] Failed to send report:', err.message)
    });
  }

  /**
   * Résume une réponse API en gardant les données utiles au debug
   */
  private summarize(data: any): any {
    if (!data) return null;
    try {
      if (Array.isArray(data)) {
        return {
          _type: 'array',
          _totalItems: data.length,
          _sample: data.slice(0, 3)
        };
      }

      if (typeof data === 'object') {
        const result: any = {};
        for (const key of Object.keys(data)) {
          if (Array.isArray(data[key])) {
            result[key] = {
              _type: 'array',
              _totalItems: data[key].length,
              _sample: data[key].slice(0, 3)
            };
          } else {
            result[key] = data[key];
          }
        }
        return result;
      }

      return data;
    } catch {
      return '[non-serializable]';
    }
  }
}
